const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const roundMoney = (value) => Number(value.toFixed(2));

/**
 * Read-only estimate from the form's reference prices, never a production posting.
 * Matches the standard-cost basis in routes/production.js:
 * material quantity × reference purchase price + variable cost × output quantity.
 * Equipment follows computeDepreciationLines, including remaining-value caps.
 * Missing prices/registry data stay unknown instead of becoming a zero cost.
 */
export const estimateProductionUnit = ({
  outputQuantity,
  materials = [],
  variableCosts = [],
  equipmentLinks = [],
  equipment = [],
  staleMaterials = false,
}) => {
  const quantity = numberOrNull(outputQuantity);
  const validQuantity = quantity !== null && quantity > 0;
  const materialValues = materials.map((line) => {
    const amount = numberOrNull(line.quantity);
    const price = numberOrNull(line.purchase_price);
    // Legacy form loading substitutes 0 for an unavailable purchase price.
    return line.product_id && amount > 0 && price > 0 ? amount * price : null;
  });
  const materialsCost = materialValues.length && materialValues.every((value) => value !== null)
    ? materialValues.reduce((sum, value) => sum + value, 0)
    : null;
  const variableValues = variableCosts.map((line) => numberOrNull(line.cost_per_unit));
  const variableCost = validQuantity && variableValues.every((value) => value !== null && value >= 0)
    ? variableValues.reduce((sum, value) => sum + value * quantity, 0)
    : null;

  const seenEquipment = new Set();
  const depreciationValues = equipmentLinks.map((link) => {
    if (!link.equipment_id) return null;
    if (seenEquipment.has(String(link.equipment_id))) return 0;
    seenEquipment.add(String(link.equipment_id));
    const item = equipment.find((entry) => String(entry.id) === String(link.equipment_id));
    if (!item || !validQuantity) return null;
    if (item.status && item.status !== "active") return 0;
    const values = [item.purchase_value, item.residual_value, item.accumulated_depreciation,
      item.depreciation_rate_per_unit, link.oeuvre_coefficient ?? 1].map(numberOrNull);
    if (values.some((value) => value === null || value < 0)) return null;
    const [purchase, residual, accumulated, rate, coefficient] = values;
    const remaining = Math.max(purchase - residual - accumulated, 0);
    const persistedCoefficient = Number(coefficient.toFixed(4));
    return roundMoney(Math.min(rate * persistedCoefficient * quantity, remaining));
  });
  const depreciationCost = depreciationValues.every((value) => value !== null)
    ? roundMoney(depreciationValues.reduce((sum, value) => sum + value, 0))
    : null;
  const complete = validQuantity && materialsCost !== null && variableCost !== null &&
    depreciationCost !== null && !staleMaterials;
  const total = complete ? materialsCost + variableCost + depreciationCost : null;
  return {
    quantity: validQuantity ? quantity : null,
    materialsCost,
    variableCost,
    depreciationCost,
    hasEquipment: equipmentLinks.length > 0,
    complete,
    total,
    unitCost: complete ? total / quantity : null,
    reason: staleMaterials ? "Les quantités de matières sont à recalibrer."
      : !validQuantity ? "Renseignez une quantité produite supérieure à zéro."
      : materialsCost === null ? "Complétez les matières, leurs quantités et leurs prix de référence."
      : variableCost === null ? "Complétez les coûts variables."
      : depreciationCost === null ? "Les données des matériels associés restent à compléter ou à charger."
      : null,
  };
};

export const formatProductionEstimate = (value, maximumFractionDigits = 0) =>
  value === null || value === undefined || !Number.isFinite(Number(value)) ? "—"
    : new Intl.NumberFormat("fr-FR", { maximumFractionDigits }).format(Number(value));
